import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, LogIn, LogOut, Phone, Search, Clock, Users, IndianRupee, X, Lock, Eye, EyeOff, UserCheck, UserMinus, CalendarDays } from 'lucide-react';
import { format, parseISO, startOfWeek, endOfWeek, isWithinInterval, startOfDay } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const roleColors = {
  Owner: 'bg-purple-100 text-purple-700',
  Manager: 'bg-blue-100 text-blue-700',
  Stylist: 'bg-green-100 text-green-700',
  Assistant: 'bg-orange-100 text-orange-700',
  Receptionist: 'bg-pink-100 text-pink-700',
  Barber: 'bg-teal-100 text-teal-700',
};

const emptyForm = {
  name: '',
  phone: '',
  role: 'Stylist',
  commission_rate: 10,
  has_login: false,
  login_email: '',
  login_password: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** When false, app access line is hidden on staff cards (data unchanged). */
const UI_SHOW_APP_ACCESS_ON_CARD = false;

function roleAccessDescription(role, t) {
  const r = (role || '').toLowerCase();
  if (r === 'owner') return t('role_access_owner');
  if (r === 'manager') return t('role_access_manager');
  if (r === 'receptionist') return t('role_access_receptionist');
  return t('role_access_stylist');
}

const formatCurrency = (value) => `${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

function formatDurationHours(hours) {
  if (hours == null || hours < 0) return '0h 0m';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatSecondsToHms(totalSeconds) {
  if (totalSeconds == null || totalSeconds < 0) return '0h 0m 0s';
  const s = Math.floor(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}h ${m}m ${sec}s`;
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? parseISO(iso.replace('Z', '+00:00')) : iso;
    return format(d, 'h:mm a');
  } catch {
    return '—';
  }
}

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  try {
    const d = parseISO(dateStr);
    return format(d, 'd MMM yyyy');
  } catch {
    return dateStr;
  }
}

function isDateInCurrentWeek(dateStr) {
  try {
    const d = parseISO(dateStr);
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    return isWithinInterval(d, { start: weekStart, end: weekEnd });
  } catch {
    return false;
  }
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function StaffCard({ staff, todayStat, onClockIn, onClockOut, onEdit, onDelete, onOpenDetail, onRefetchTodayStats, t }) {
  const [liveSeconds, setLiveSeconds] = useState(0);
  const intervalRef = useRef(null);
  const isClockedIn = todayStat?.is_clocked_in ?? staff.is_clocked_in;
  const clockInTime = todayStat?.clock_in_time ?? staff.clock_in_time;
  const previousSessionsHours = todayStat?.previous_sessions_hours ?? 0;
  const previousSessionsSeconds =
    todayStat?.previous_sessions_seconds != null && todayStat?.previous_sessions_seconds !== ''
      ? Number(todayStat.previous_sessions_seconds)
      : Math.round((Number(previousSessionsHours) || 0) * 3600);
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const computeLiveSeconds = useCallback(() => {
    if (!isClockedIn || !clockInTime) return 0;
    const previousSeconds = Number.isFinite(previousSessionsSeconds) ? Math.max(0, Math.floor(previousSessionsSeconds)) : 0;
    try {
      const start = typeof clockInTime === 'string' ? parseISO(clockInTime.replace('Z', '+00:00')) : clockInTime;
      const currentElapsedSeconds = Math.max(0, Math.floor((Date.now() - start.getTime()) / 1000));
      return previousSeconds + currentElapsedSeconds;
    } catch {
      return previousSeconds;
    }
  }, [isClockedIn, clockInTime, previousSessionsSeconds]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!isClockedIn || !clockInTime) {
      setLiveSeconds(0);
      return;
    }
    try {
      const clockInDate = typeof clockInTime === 'string' ? parseISO(clockInTime.replace('Z', '+00:00')) : clockInTime;
      const clockInDateStr = format(startOfDay(clockInDate), 'yyyy-MM-dd');
      if (clockInDateStr !== todayStr && onRefetchTodayStats) {
        onRefetchTodayStats(staff.id);
      }
    } catch (_) {}
    setLiveSeconds(computeLiveSeconds());
    intervalRef.current = setInterval(() => {
      setLiveSeconds(computeLiveSeconds());
    }, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isClockedIn, clockInTime, staff.id, todayStr, onRefetchTodayStats, computeLiveSeconds]);

  const liveDisplay = formatSecondsToHms(liveSeconds);

  const handleCardClick = (e) => {
    if (e.target.closest('button')) return;
    onOpenDetail(staff);
  };

  const initial = (staff.name?.[0] || 'S').toUpperCase();

  return (
    <Card
      className={`border border-gray-100 shadow-sm rounded-[12px] hover:shadow-lg transition-[box-shadow] duration-200 ease-out cursor-pointer overflow-hidden ${isClockedIn ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-gray-200'}`}
      onClick={handleCardClick}
    >
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-2 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-14 h-14 bg-[#1a1a2e] rounded-2xl flex items-center justify-center text-white font-bold text-xl shrink-0">
              {initial}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-lg text-[#1a1a2e] truncate">{staff.name}</h3>
              <Badge className="bg-green-100 text-green-700 mt-0.5">{staff.role}</Badge>
            </div>
          </div>
          <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg min-w-8 min-h-8" data-testid={`edit-staff-${staff.id}`} onClick={() => onEdit(staff)}>
              <Edit className="w-4 h-4 text-gray-400" />
            </Button>
            <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg min-w-8 min-h-8" data-testid={`delete-staff-${staff.id}`} onClick={() => onDelete(staff.id)}>
              <Trash2 className="w-4 h-4 text-red-400" />
            </Button>
          </div>
        </div>

        <div className="space-y-1 mb-4 pb-4 border-b border-gray-100">
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <Phone className="w-4 h-4 shrink-0 text-gray-400" /> {staff.phone}
          </p>
          {UI_SHOW_APP_ACCESS_ON_CARD && (
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 shrink-0" />
              <span>
                {t('app_access')}:{' '}
                {staff.has_login ? (
                  <span className="text-green-700 font-medium">{t('app_access_card_active')}</span>
                ) : (
                  <span>{t('app_access_card_off')}</span>
                )}
              </span>
            </p>
          )}
          <p className="text-sm text-gray-500">
            Commission: <span className="font-medium text-[#1a1a2e]">{staff.commission_rate ?? 0}%</span>
          </p>
        </div>

        <div className="mb-4">
          <div className="flex flex-wrap sm:flex-nowrap items-stretch divide-x divide-gray-200/90">
            <div className="flex-1 min-w-[88px] px-2 sm:px-3 py-1 first:pl-0 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-1.5 text-[#1a1a2e] font-bold text-xl leading-tight">
                <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="tabular-nums">{formatDurationHours(todayStat?.hours_today ?? 0)}</span>
              </div>
              <p className="text-[13px] text-gray-500 mt-1">{t('staff_stat_label_today')}</p>
            </div>
            <div className="flex-1 min-w-[88px] px-2 sm:px-3 py-1 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-1.5 text-[#1a1a2e] font-bold text-xl leading-tight">
                <Users className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="tabular-nums">{todayStat?.customers_served ?? 0}</span>
              </div>
              <p className="text-[13px] text-gray-500 mt-1">{t('staff_stat_label_served')}</p>
            </div>
            <div className="flex-1 min-w-[88px] px-2 sm:px-3 py-1 last:pr-0 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-1.5 text-[#1a1a2e] font-bold text-xl leading-tight">
                <IndianRupee className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="tabular-nums">{formatCurrency(todayStat?.revenue_today)}</span>
              </div>
              <p className="text-[13px] text-gray-500 mt-1">{t('revenue')}</p>
            </div>
          </div>
        </div>

        <div className="space-y-2.5" onClick={(e) => e.stopPropagation()}>
          {isClockedIn ? (
            <Button
              data-testid={`clock-out-${staff.id}`}
              onClick={() => onClockOut(staff.id)}
              className="w-full h-10 rounded-[10px] text-sm font-medium bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 gap-2 min-h-0 py-0 transition-colors duration-200 ease-out"
              variant="ghost"
            >
              <LogOut className="w-4 h-4" /> {t('staff_out')}
            </Button>
          ) : (
            <Button
              data-testid={`clock-in-${staff.id}`}
              onClick={() => onClockIn(staff.id)}
              className="w-full h-10 rounded-[10px] text-sm font-medium bg-white text-green-700 hover:bg-green-50/80 border-2 border-green-500 gap-2 min-h-0 py-0 transition-colors duration-200 ease-out"
              variant="outline"
            >
              <LogIn className="w-4 h-4" /> {t('staff_in')}
            </Button>
          )}
          {isClockedIn && (
            <div className="inline-flex items-center gap-2 rounded-full bg-[#E8F5E9]/80 px-3 py-1.5 text-[13px] font-medium text-green-800 transition-colors duration-200 ease-out">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
              <span>
                {t('staff_on_duty')} · <span className="tabular-nums text-green-900">{liveDisplay}</span>
              </span>
            </div>
          )}
          {!isClockedIn && (
            <div className="flex items-center gap-2 text-gray-500 text-[13px] font-medium">
              <span className="w-2 h-2 rounded-full bg-gray-400 shrink-0" />
              {t('staff_off_duty')}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function DayCell({ day, todayStr, rowStaffId, rowStaffName, popoverDay, setPopoverDay, onOverride, t, formatTime, formatDurationHours, formatDateShort }) {
  const isToday = day.date === todayStr;
  const isOpen = popoverDay?.staffId === rowStaffId && popoverDay?.date === day.date;

  let bg = 'bg-gray-100';
  let symbol = '—';
  if (day.day_type === 'future') {
    bg = 'bg-gray-200';
    symbol = '';
  } else if (day.day_type === 'holiday') {
    bg = 'bg-blue-500 text-white';
    symbol = '🎉';
  } else if (day.day_type === 'full') {
    bg = 'bg-green-500 text-white';
    symbol = '✅';
  } else if (day.day_type === 'half') {
    bg = 'bg-amber-400 text-white';
    symbol = '🌓';
  } else if (day.day_type === 'in_progress') {
    bg = 'bg-amber-300 text-amber-900';
    symbol = '🟡';
  } else if (day.day_type === 'short') {
    bg = 'bg-red-400 text-white';
    symbol = '🔴';
  } else if (day.day_type === 'absent') {
    bg = 'bg-pink-100 text-pink-700';
    symbol = '⭕';
  }

  return (
    <td className={`p-0.5 ${isToday ? 'ring-2 ring-[#F5A623] ring-inset rounded' : ''}`}>
      <Popover open={isOpen} onOpenChange={(open) => !open && setPopoverDay(null)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`min-w-[32px] min-h-[32px] w-8 h-8 rounded flex items-center justify-center text-sm font-semibold transition-[transform] duration-200 ease-out hover:scale-105 ${bg} ${day.day_type === 'future' ? 'cursor-default' : 'cursor-pointer'} focus:outline-none focus:ring-2 focus:ring-[#F5A623] focus:ring-offset-1 ${isToday ? 'font-bold' : ''}`}
            onClick={() => day.day_type !== 'future' && setPopoverDay({ staffId: rowStaffId, staffName: rowStaffName, ...day })}
          >
            {symbol}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 rounded-xl" align="center">
          <div className="space-y-2">
            <p className="font-semibold text-[#1a1a2e]">
              {rowStaffName} — {formatDateShort(day.date)}
            </p>
            <p className="text-sm text-gray-600">
              {t('clock_in')}: {formatTime(day.clock_in)} · {t('clock_out')}: {formatTime(day.clock_out)}
            </p>
            <p className="text-sm text-gray-600">
              Total: {formatDurationHours(day.total_hours)}
            </p>
            <div className="flex gap-2 pt-2">
              <Button size="sm" variant="outline" className="rounded-lg min-h-[44px]" onClick={() => onOverride(rowStaffId, day.date, 'holiday')}>
                {t('mark_holiday')}
              </Button>
              <Button size="sm" variant="outline" className="rounded-lg text-red-600 min-h-[44px]" onClick={() => onOverride(rowStaffId, day.date, 'absent')}>
                {t('mark_absent')}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </td>
  );
}

function StaffDetailPanel({ staff, onClose, t, formatCurrency, formatDurationHours, formatTime, formatDateShort }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [performancePeriod, setPerformancePeriod] = useState('month');
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState(false);
  const [panelMonth, setPanelMonth] = useState(new Date().getMonth() + 1);
  const [panelYear, setPanelYear] = useState(new Date().getFullYear());
  const [overviewError, setOverviewError] = useState(null);
  const [performanceError, setPerformanceError] = useState(null);

  useEffect(() => {
    if (!staff?.id) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [staff?.id]);

  useEffect(() => {
    if (!staff?.id) return;
    setOverview(null);
    setOverviewError(null);
    api.get(`/staff/${staff.id}/overview`)
      .then((r) => setOverview(r?.data ?? null))
      .catch((err) => setOverviewError(err?.response?.data || err?.message || 'Failed to load overview'));
  }, [staff?.id]);

  const fetchAttendanceData = useCallback(async () => {
    if (!staff?.id) return;
    setAttendanceLoading(true);
    setAttendanceError(false);
    try {
      const response = await api.get('/attendance/summary', {
        params: {
          staff_id: staff.id,
          month: panelMonth,
          year: panelYear,
        },
      });
      console.log('Attendance data:', response.data);
      const data = Array.isArray(response?.data) && response.data.length > 0 ? response.data[0] : response?.data ?? null;
      setAttendanceData(data);
    } catch (error) {
      console.error('Attendance fetch error:', error.response?.data || error.message);
      setAttendanceError(true);
      setAttendanceData(null);
    } finally {
      setAttendanceLoading(false);
    }
  }, [staff?.id, panelMonth, panelYear]);

  useEffect(() => {
    if (staff && activeTab === 'attendance') {
      fetchAttendanceData();
    }
  }, [staff?.id, activeTab, panelMonth, panelYear, fetchAttendanceData]);

  useEffect(() => {
    if (!staff?.id) return;
    setPerformance(null);
    setPerformanceError(null);
    api.get(`/staff/${staff.id}/performance`, { params: { period: performancePeriod } })
      .then((r) => setPerformance(r?.data ?? null))
      .catch((err) => setPerformanceError(err?.response?.data || err?.message || 'Failed to load performance'));
  }, [staff?.id, performancePeriod]);

  if (!staff) return null;

  return createPortal(
    <>
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 49,
        }}
      />
      <div
        className="w-full max-w-[480px] bg-[#faf8f5] shadow-2xl flex flex-col overflow-hidden transition-transform duration-300 ease-out"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          height: '100%',
          zIndex: 50,
          transform: 'translateX(0)',
        }}
      >
        <div className="flex-shrink-0 bg-[#1a1a2e] p-4 pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-[#f0a500] text-[#1a1a2e] flex items-center justify-center font-bold text-xl shrink-0">
                {(staff.name?.[0] || 'S').toUpperCase()}
              </div>
              <div>
                <h2 className="text-lg font-bold text-white font-poppins">{staff.name}</h2>
                <Badge className="bg-[#f0a500] text-[#1a1a2e] mt-1">{staff.role}</Badge>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-xl min-w-10 min-h-10" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-gray-300 text-sm mt-2 flex items-center gap-2">
            <Phone className="w-4 h-4" /> {staff.phone}
          </p>
          <p className="text-gray-400 text-sm">Commission: {staff.commission_rate ?? 0}%</p>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-3 rounded-xl h-11 bg-[#2d2d44] p-1">
              <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-[#f0a500] data-[state=active]:text-[#1a1a2e] text-gray-300">
                {t('staff_overview')}
              </TabsTrigger>
              <TabsTrigger value="attendance" className="rounded-lg data-[state=active]:bg-[#f0a500] data-[state=active]:text-[#1a1a2e] text-gray-300">
                {t('staff_attendance')}
              </TabsTrigger>
              <TabsTrigger value="performance" className="rounded-lg data-[state=active]:bg-[#f0a500] data-[state=active]:text-[#1a1a2e] text-gray-300">
                {t('staff_performance')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto -webkit-overflow-scrolling-touch p-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
          <TabsContent value="overview" className="mt-0 h-full">
          {(
            <>
              {overviewError && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3 mb-4">{overviewError}</p>
              )}
              {overview ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Card className="rounded-xl border border-gray-100">
                      <CardContent className="p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('customers_served')}</p>
                        <p className="text-xl font-semibold text-[#1a1a2e]">{overview.total_customers_served ?? 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-xl border border-gray-100">
                      <CardContent className="p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('revenue_today').replace(' today', '')}</p>
                        <p className="text-xl font-semibold text-[#1a1a2e]">{formatCurrency(overview.total_revenue_generated)}</p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-xl border border-gray-100">
                      <CardContent className="p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('all_time_hours')}</p>
                        <p className="text-xl font-semibold text-[#1a1a2e]">{formatDurationHours(overview.total_hours_worked)}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('joining_date')}: {overview.joining_date ? formatDateShort(String(overview.joining_date).split('T')[0]) : '—'}
                  </p>
                  <p className="text-sm font-semibold text-[#1a1a2e] mt-2">This Month</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Card className="rounded-xl border border-gray-100">
                      <CardContent className="p-3">
                        <p className="text-xs text-gray-500">Customers</p>
                        <p className="text-lg font-semibold text-[#1a1a2e]">{overview.total_customers_served ?? 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-xl border border-gray-100">
                      <CardContent className="p-3">
                        <p className="text-xs text-gray-500">Revenue</p>
                        <p className="text-lg font-semibold text-[#1a1a2e]">{formatCurrency(overview.total_revenue_generated)}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ) : (
                <Skeleton className="h-48 rounded-xl" />
              )}
            </>
          )}
          </TabsContent>

          <TabsContent value="attendance" className="mt-0">
            <div className="space-y-4">
              <Select
                value={`${panelYear}-${panelMonth}`}
                onValueChange={(v) => {
                  const [y, m] = v.split('-').map(Number);
                  setPanelYear(y);
                  setPanelMonth(m);
                }}
              >
                <SelectTrigger className="w-full max-w-[200px] h-12 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 1, 2, 3, 4, 5].map((i) => {
                    const d = new Date();
                    d.setMonth(d.getMonth() - i);
                    const y = d.getFullYear();
                    const m = d.getMonth() + 1;
                    return (
                      <SelectItem key={`${y}-${m}`} value={`${y}-${m}`}>
                        {MONTH_NAMES[m - 1]} {y}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              {attendanceLoading && (
                <div className="space-y-3">
                  <Skeleton className="h-20 rounded-xl" />
                  <Skeleton className="h-32 rounded-xl" />
                </div>
              )}

              {!attendanceLoading && attendanceError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center space-y-3">
                  <p className="text-sm text-red-700">Could not load attendance data. Please try again.</p>
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => fetchAttendanceData()}>
                    Retry
                  </Button>
                </div>
              )}

              {!attendanceLoading && !attendanceError && attendanceData?.summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card className="rounded-xl">
                    <CardContent className="p-3">
                      <p className="text-xs text-gray-500">{t('hours_this_month')}</p>
                      <p className="font-semibold text-[#1a1a2e]">{formatDurationHours(attendanceData.summary.total_hours)}</p>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl">
                    <CardContent className="p-3">
                      <p className="text-xs text-gray-500">{t('full_days')}</p>
                      <p className="font-semibold text-[#1a1a2e]">{attendanceData.summary.full_days ?? 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl">
                    <CardContent className="p-3">
                      <p className="text-xs text-gray-500">{t('half_days')}</p>
                      <p className="font-semibold text-[#1a1a2e]">{attendanceData.summary.half_days ?? 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl">
                    <CardContent className="p-3">
                      <p className="text-xs text-gray-500">{t('avg_per_day')}</p>
                      <p className="font-semibold text-[#1a1a2e]">{attendanceData.summary.avg_hours_per_day != null ? `${Number(attendanceData.summary.avg_hours_per_day).toFixed(1)}h` : '0h'}</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {!attendanceLoading && !attendanceError && attendanceData?.days && (() => {
                const dayMap = {};
                attendanceData.days.forEach((d) => { dayMap[d.date] = d; });
                const firstDay = new Date(panelYear, panelMonth - 1, 1);
                const pad = (firstDay.getDay() + 6) % 7;
                const daysInMonth = new Date(panelYear, panelMonth, 0).getDate();
                const todayStr = format(new Date(), 'yyyy-MM-dd');
                const cellRows = [];
                for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
                  const dateStr = `${panelYear}-${String(panelMonth).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                  const dayData = dayMap[dateStr];
                  const dayType = dayData?.day_type;
                  const isPast = dateStr < todayStr;
                  const isFuture = dateStr > todayStr;
                  let cellBg = 'bg-gray-100';
                  let symbol = String(dayNum);
                  if (dayType === 'full') { cellBg = 'bg-green-500 text-white'; symbol = dayNum; }
                  else if (dayType === 'half') { cellBg = 'bg-amber-400 text-white'; symbol = dayNum; }
                  else if (dayType === 'in_progress') { cellBg = 'bg-amber-300 text-amber-900'; symbol = dayNum; }
                  else if (dayType === 'short') { cellBg = 'bg-red-400 text-white'; symbol = dayNum; }
                  else if (dayType === 'holiday') { cellBg = 'bg-blue-500 text-white'; symbol = dayNum; }
                  else if (isFuture) { cellBg = 'bg-gray-200'; symbol = dayNum; }
                  else if (isPast && (dayType == null || dayType === 'absent')) { cellBg = 'bg-pink-100 text-pink-700'; symbol = '○'; }
                  cellRows.push({ dateStr, dayNum, cellBg, symbol });
                }
                const leadingBlanks = Array(pad).fill(null);
                const allCells = [...leadingBlanks, ...cellRows];
                return (
                  <div className="grid grid-cols-7 gap-1">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                      <div key={d} className="text-center text-xs font-semibold text-gray-500 p-1">
                        {d}
                      </div>
                    ))}
                    {allCells.map((cell, idx) => (
                      cell === null ? (
                        <div key={`pad-${idx}`} className="aspect-square rounded" />
                      ) : (
                        <div
                          key={cell.dateStr}
                          className={`aspect-square rounded flex items-center justify-center text-xs font-medium ${cell.cellBg}`}
                          title={cell.dateStr}
                        >
                          {cell.symbol}
                        </div>
                      )
                    ))}
                  </div>
                );
              })()}
            </div>
          </TabsContent>

          <TabsContent value="performance" className="mt-0">
          {(
            <>
              <div className="flex gap-2 mb-4">
                {['month', 'week', 'today'].map((p) => (
                  <Button
                    key={p}
                    variant={performancePeriod === p ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-xl min-h-[44px]"
                    onClick={() => setPerformancePeriod(p)}
                  >
                    {p === 'month' ? t('hours_this_month') : p === 'week' ? t('hours_this_week') : t('hours_today')}
                  </Button>
                ))}
              </div>
              {performanceError && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3 mb-4">{performanceError}</p>
              )}
              {performance ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Card className="rounded-xl border border-gray-100">
                      <CardContent className="p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Customers</p>
                        <p className="text-xl font-semibold text-[#1a1a2e]">{performance.customers_served ?? 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-xl border border-gray-100">
                      <CardContent className="p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Revenue</p>
                        <p className="text-xl font-semibold text-[#1a1a2e]">{formatCurrency(performance.revenue_generated)}</p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-xl border border-gray-100">
                      <CardContent className="p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Commission</p>
                        <p className="text-xl font-semibold text-[#1a1a2e]">{formatCurrency(performance.commission_earned)}</p>
                      </CardContent>
                    </Card>
                  </div>
                  {performance.top_services && performance.top_services.length > 0 && (
                    <div>
                      <p className="font-semibold text-[#1a1a2e] mb-2">Top Services</p>
                      <ul className="space-y-2 text-sm">
                        {performance.top_services.map((svc, i) => (
                          <li key={i} className="flex justify-between items-center py-1 border-b border-gray-100">
                            <span className="text-[#1a1a2e]">{svc.name}</span>
                            <span className="text-gray-600">{svc.count} × {formatCurrency(svc.revenue)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(performance.daily_revenue) && performance.daily_revenue.length > 0 && (
                    <div className="mt-4">
                      <p className="font-semibold text-[#1a1a2e] mb-2">Revenue last 7 days</p>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={performance.daily_revenue.map((x) => {
                              try {
                                const dateLabel = x?.date ? format(parseISO(String(x.date).replace('Z', '+00:00')), 'dd/MM') : '—';
                                return { ...x, dateLabel, revenue: Number(x?.revenue ?? 0) };
                              } catch {
                                return { ...x, dateLabel: '—', revenue: Number(x?.revenue ?? 0) };
                              }
                            })}
                            margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}`} />
                            <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} labelFormatter={(l) => l} />
                            <Bar dataKey="revenue" fill="#f0a500" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Skeleton className="h-48 rounded-xl" />
              )}
            </>
          )}
          </TabsContent>
          </Tabs>
        </div>
      </div>
    </>,
    document.body
  );
}

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [todayStats, setTodayStats] = useState({});
  const [attendanceSummary, setAttendanceSummary] = useState([]);
  const [attendanceMonth, setAttendanceMonth] = useState(new Date().getMonth() + 1);
  const [attendanceYear, setAttendanceYear] = useState(new Date().getFullYear());
  const [attendanceStaffId, setAttendanceStaffId] = useState('');
  const [attendanceMonthPickerOpen, setAttendanceMonthPickerOpen] = useState(false);
  const [staffSummaryFilter, setStaffSummaryFilter] = useState('all');
  const [detailStaff, setDetailStaff] = useState(null);
  const [popoverDay, setPopoverDay] = useState(null);
  const [hadLoginWhenOpened, setHadLoginWhenOpened] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showAppAccessPasswordField, setShowAppAccessPasswordField] = useState(false);
  const [removeLoginConfirmOpen, setRemoveLoginConfirmOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();

  const loadStaff = useCallback(async () => {
    try {
      const r = await api.get('/staff');
      setStaff(Array.isArray(r?.data) ? r.data : []);
    } catch {
      toast.error(t('staff_load_error'));
      setStaff([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    if (searchParams.get('addStaff') === '1') {
      setEditingId(null);
      setHadLoginWhenOpened(false);
      setForm({ ...emptyForm });
      setShowAppAccessPasswordField(false);
      setShowLoginPassword(false);
      setDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('addStaff');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !Array.isArray(staff) || staff.length === 0) return;
    const s = staff.find((x) => x.id === editId);
    if (s) {
      setEditingId(s.id);
      setHadLoginWhenOpened(!!s.has_login);
      setForm({
        name: s.name,
        phone: s.phone,
        role: s.role,
        commission_rate: s.commission_rate ?? 10,
        has_login: !!s.has_login,
        login_email: s.login_email || '',
        login_password: '',
      });
      setShowAppAccessPasswordField(false);
      setShowLoginPassword(false);
      setDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, staff, setSearchParams]);

  useEffect(() => {
    if (!Array.isArray(staff) || staff.length === 0) {
      setTodayStats({});
      return;
    }
    let cancelled = false;
    const load = async () => {
      const map = {};
      await Promise.all(
        staff.map(async (s) => {
          try {
            const res = await api.get(`/staff/${s.id}/today-stats`);
            if (!cancelled) map[s.id] = res.data;
          } catch {
            if (!cancelled) map[s.id] = { customers_served: 0, revenue_today: 0, hours_today: 0, previous_sessions_seconds: 0, previous_sessions_hours: 0, is_clocked_in: false, clock_in_time: null };
          }
        })
      );
      if (!cancelled) setTodayStats(map);
    };
    load();
    return () => { cancelled = true; };
  }, [staff]);

  const refetchTodayStatsForStaff = useCallback(async (staffId) => {
    try {
      const res = await api.get(`/staff/${staffId}/today-stats`);
      setTodayStats((prev) => ({ ...prev, [staffId]: res.data }));
    } catch (_) {}
  }, []);

  const loadAttendanceSummary = useCallback(async () => {
    try {
      const params = { month: attendanceMonth, year: attendanceYear };
      if (attendanceStaffId) params.staff_id = attendanceStaffId;
      const r = await api.get('/attendance/summary', { params });
      setAttendanceSummary(Array.isArray(r?.data) ? r.data : []);
    } catch {
      setAttendanceSummary([]);
    }
  }, [attendanceMonth, attendanceYear, attendanceStaffId]);

  useEffect(() => {
    loadAttendanceSummary();
  }, [loadAttendanceSummary]);

  useEffect(() => {
    const now = new Date();
    const cy = now.getFullYear();
    const cm = now.getMonth() + 1;
    const cap = attendanceYear === cy ? cm : 12;
    if (attendanceMonth > cap) {
      setAttendanceMonth(cap);
    }
  }, [attendanceYear, attendanceMonth]);

  const handleSave = async () => {
    if (!form.name || !form.phone) {
      toast.error(t('staff_req'));
      return;
    }
    if (form.has_login) {
      const em = (form.login_email || '').trim();
      if (!em || !EMAIL_REGEX.test(em)) {
        toast.error(t('val_email_invalid'));
        return;
      }
      const needPassword = !editingId || (editingId && !hadLoginWhenOpened);
      const pw = (form.login_password || '').trim();
      if (needPassword && (!pw || pw.length < 8)) {
        toast.error(t('val_password_min'));
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        role: form.role,
        commission_rate: Number(form.commission_rate),
        has_login: form.has_login,
        login_email: form.has_login ? (form.login_email || '').trim() : null,
      };
      const pw = (form.login_password || '').trim();
      if (form.has_login && pw) payload.login_password = pw;
      if (editingId) {
        await api.put(`/staff/${editingId}`, payload);
        toast.success(t('staff_updated'));
      } else {
        await api.post('/staff', payload);
        toast.success(t('staff_added'));
      }
      setDialogOpen(false);
      setForm({ ...emptyForm });
      setEditingId(null);
      setHadLoginWhenOpened(false);
      setShowAppAccessPasswordField(false);
      setShowLoginPassword(false);
      loadStaff();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(Array.isArray(msg) ? msg[0]?.msg || msg[0] : msg || t('staff_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleClockIn = async (id) => {
    try {
      await api.post(`/staff/${id}/clock-in`);
      toast.success(t('staff_clocked_in'));
      loadStaff();
      const res = await api.get(`/staff/${id}/today-stats`);
      setTodayStats((prev) => ({ ...prev, [id]: res.data }));
    } catch {
      toast.error(t('staff_clock_in_error'));
    }
  };

  const handleClockOut = async (id) => {
    try {
      await api.post(`/staff/${id}/clock-out`);
      toast.success(t('staff_clocked_out'));
      loadStaff();
      const res = await api.get(`/staff/${id}/today-stats`);
      setTodayStats((prev) => ({ ...prev, [id]: res.data }));
    } catch {
      toast.error(t('staff_clock_out_error'));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/staff/${deleteId}`);
      toast.success(t('staff_removed'));
      setDeleteId(null);
      loadStaff();
    } catch {
      toast.error(t('staff_delete_error'));
    }
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setHadLoginWhenOpened(!!s.has_login);
    setForm({
      name: s.name,
      phone: s.phone,
      role: s.role,
      commission_rate: s.commission_rate ?? 10,
      has_login: !!s.has_login,
      login_email: s.login_email || '',
      login_password: '',
    });
    setShowAppAccessPasswordField(false);
    setShowLoginPassword(false);
    setDialogOpen(true);
  };

  const handleRemoveStaffLogin = async () => {
    if (!editingId) return;
    try {
      await api.delete(`/staff/${editingId}/login`);
      toast.success(t('staff_updated'));
      setForm((f) => ({ ...f, has_login: false, login_password: '' }));
      setHadLoginWhenOpened(false);
      setRemoveLoginConfirmOpen(false);
      setShowAppAccessPasswordField(false);
      loadStaff();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(Array.isArray(msg) ? msg[0] : msg || t('staff_save_error'));
    }
  };

  const handleOverride = async (staffId, date, overrideStatus) => {
    try {
      await api.patch('/attendance/override', { staff_id: staffId, date, override_status: overrideStatus });
      toast.success(overrideStatus === 'holiday' ? t('mark_holiday') : t('mark_absent'));
      setPopoverDay(null);
      loadAttendanceSummary();
    } catch {
      toast.error('Failed to update');
    }
  };

  const trimmedSearch = searchTerm.trim();
  const searchLower = trimmedSearch.toLowerCase();
  const searchDigits = trimmedSearch.replace(/\D/g, '');

  const filtered = staff.filter((s) => {
    const matchesSearch =
      !trimmedSearch ||
      (s.name?.toLowerCase().includes(searchLower)) ||
      (searchDigits.length > 0 && String(s.phone ?? '').replace(/\D/g, '').includes(searchDigits));
    if (!matchesSearch) return false;
    if (staffSummaryFilter === 'all') return true;
    const onDuty = todayStats[s.id]?.is_clocked_in ?? s.is_clocked_in;
    if (staffSummaryFilter === 'on_duty') return !!onDuty;
    return !onDuty;
  });

  const onDutyCount = staff.filter((s) => (todayStats[s.id]?.is_clocked_in ?? s.is_clocked_in)).length;
  const offDutyCount = staff.length - onDutyCount;
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const nowCalendar = new Date();
  const maxAttendanceYear = nowCalendar.getFullYear();
  const maxAttendanceMonth = nowCalendar.getMonth() + 1;
  const minAttendanceYear = maxAttendanceYear - 6;
  const attendanceYearOptions = [];
  for (let y = maxAttendanceYear; y >= minAttendanceYear; y -= 1) {
    attendanceYearOptions.push(y);
  }
  const maxMonthForSelectedYear =
    attendanceYear === maxAttendanceYear ? maxAttendanceMonth : 12;
  const attendanceMonthOptions = Array.from({ length: maxMonthForSelectedYear }, (_, i) => i + 1);

  if (loading) {
    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" />
          <Input
            data-testid="staff-search"
            placeholder={t('staff_search')}
            className="h-11 pl-10 pr-4 text-base rounded-[10px] border border-[#E5E7EB] bg-white shadow-sm transition-[box-shadow,border-color] duration-200 ease-out focus-visible:border-[#F5A623] focus-visible:ring-2 focus-visible:ring-[#F5A623]/25 focus-visible:ring-offset-0 md:text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button
          data-testid="add-staff-btn"
          onClick={() => {
            setEditingId(null);
            setHadLoginWhenOpened(false);
            setForm({ ...emptyForm });
            setShowAppAccessPasswordField(false);
            setShowLoginPassword(false);
            setDialogOpen(true);
          }}
          className="h-11 px-5 rounded-[10px] bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] text-base font-[600] gap-2 min-h-0 shrink-0 transition-colors duration-200 ease-out"
        >
          <Plus className="w-5 h-5 shrink-0" /> {t('staff_add')}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          type="button"
          onClick={() => setStaffSummaryFilter('all')}
          aria-pressed={staffSummaryFilter === 'all'}
          className={`rounded-[12px] px-6 py-5 bg-white border border-gray-100 shadow-sm transition-all duration-200 ease-out hover:shadow-md text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623] focus-visible:ring-offset-2 ${
            staffSummaryFilter === 'all' ? 'ring-2 ring-[#F5A623] ring-offset-2' : ''
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1a1a2e] flex items-center justify-center shrink-0">
              <Users className="w-[18px] h-[18px] text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[#1a1a2e]">{t('staff_total')}</p>
              <p className="text-[32px] font-bold leading-none text-[#1a1a2e] mt-2 tabular-nums">{staff.length}</p>
              <p className="text-[13px] text-gray-500 mt-1">{t('staff_sub_team_members')}</p>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setStaffSummaryFilter('on_duty')}
          aria-pressed={staffSummaryFilter === 'on_duty'}
          className={`rounded-[12px] px-6 py-5 border-l-4 border-l-green-500 bg-[#E8F5E9]/90 border border-green-100/80 shadow-sm transition-all duration-200 ease-out hover:shadow-md text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623] focus-visible:ring-offset-2 ${
            staffSummaryFilter === 'on_duty' ? 'ring-2 ring-[#F5A623] ring-offset-2' : ''
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#C8E6C9] flex items-center justify-center shrink-0">
              <UserCheck className="w-[18px] h-[18px] text-green-700" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-green-800">{t('staff_on_duty_now')}</p>
              <p className="text-[32px] font-bold leading-none text-green-700 mt-2 tabular-nums">{onDutyCount}</p>
              <p className="text-[13px] text-green-800/70 mt-1">{t('staff_sub_currently_working')}</p>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setStaffSummaryFilter('off_duty')}
          aria-pressed={staffSummaryFilter === 'off_duty'}
          className={`rounded-[12px] px-6 py-5 bg-[#F5F5F5] border border-gray-200/80 shadow-sm transition-all duration-200 ease-out hover:shadow-md text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623] focus-visible:ring-offset-2 ${
            staffSummaryFilter === 'off_duty' ? 'ring-2 ring-[#F5A623] ring-offset-2' : ''
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
              <UserMinus className="w-[18px] h-[18px] text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[#1a1a2e]">{t('staff_off_duty_now')}</p>
              <p className="text-[32px] font-bold leading-none text-[#1a1a2e] mt-2 tabular-nums">{offDutyCount}</p>
              <p className="text-[13px] text-gray-500 mt-1">{t('staff_sub_not_clocked')}</p>
            </div>
          </div>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.length === 0 ? (
          <div className="col-span-full text-center text-gray-400 py-12 text-lg">
            {t('staff_none')}
          </div>
        ) : (
          filtered.map((s) => (
            <StaffCard
              key={s.id}
              staff={s}
              todayStat={todayStats[s.id]}
              onClockIn={handleClockIn}
              onClockOut={handleClockOut}
              onEdit={openEdit}
              onDelete={setDeleteId}
              onOpenDetail={setDetailStaff}
              onRefetchTodayStats={refetchTodayStatsForStaff}
              t={t}
            />
          ))
        )}
      </div>

      <section className="mt-8 pt-8 border-t border-gray-200/90">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-5">
          <h2 className="text-[20px] font-bold text-[#1a1a2e] font-poppins tracking-tight">{t('attendance_overview')}</h2>
          <div className="flex flex-wrap gap-2 justify-end lg:ml-auto">
            <Popover open={attendanceMonthPickerOpen} onOpenChange={setAttendanceMonthPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="attendance-month-picker"
                  className="w-[min(100%,240px)] h-10 justify-start gap-2 rounded-md border border-input bg-white px-3 text-sm font-normal shadow-sm transition-shadow duration-200 ease-out hover:bg-gray-50/80"
                >
                  <CalendarDays className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                  <span className="truncate">
                    {MONTH_NAMES[attendanceMonth - 1]} {attendanceYear}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto min-w-[260px] p-4" align="end">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                  {t('attendance_month_year_title')}
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="flex-1 space-y-1.5">
                    <Label htmlFor="attendance-year-select" className="text-xs text-gray-600">
                      {t('attendance_year_label')}
                    </Label>
                    <Select
                      value={String(attendanceYear)}
                      onValueChange={(v) => {
                        const y = Number(v);
                        setAttendanceYear(y);
                        const cap = y === maxAttendanceYear ? maxAttendanceMonth : 12;
                        setAttendanceMonth((m) => Math.min(m, cap));
                      }}
                    >
                      <SelectTrigger id="attendance-year-select" className="h-10 w-full rounded-md text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {attendanceYearOptions.map((y) => (
                          <SelectItem key={y} value={String(y)}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <Label htmlFor="attendance-month-select" className="text-xs text-gray-600">
                      {t('attendance_month_label')}
                    </Label>
                    <Select
                      value={String(Math.min(attendanceMonth, maxMonthForSelectedYear))}
                      onValueChange={(v) => setAttendanceMonth(Number(v))}
                    >
                      <SelectTrigger id="attendance-month-select" className="h-10 w-full rounded-md text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {attendanceMonthOptions.map((m) => (
                          <SelectItem key={m} value={String(m)}>
                            {MONTH_NAMES[m - 1]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Select value={attendanceStaffId || 'all'} onValueChange={(v) => setAttendanceStaffId(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[min(100%,200px)] h-10 rounded-md border border-input bg-white text-sm shadow-sm transition-shadow duration-200 ease-out hover:bg-gray-50/80">
                <SelectValue placeholder={t('all_staff')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all_staff')}</SelectItem>
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-x-auto rounded-[12px] border border-gray-200 bg-white shadow-sm">
          <table className="w-full min-w-[600px] border-collapse">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left p-3 text-sm font-semibold text-[#1a1a2e] sticky left-0 z-20 min-w-[120px] max-w-[120px] bg-gray-50 shadow-[2px_0_0_0_rgba(0,0,0,0.03)]">
                  Staff
                </th>
                {attendanceSummary[0]?.days?.map((d) => {
                  const dayNum = d.date.split('-')[2];
                  const isToday = d.date === todayStr;
                  return (
                    <th
                      key={d.date}
                      className={`p-2 text-center text-sm min-w-[36px] w-10 align-middle ${isToday ? 'ring-2 ring-[#F5A623] ring-inset rounded font-bold text-[#1a1a2e] bg-amber-50/50' : 'text-gray-600 font-medium'}`}
                    >
                      {dayNum}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {attendanceSummary.map((row, rowIdx) => {
                const rowBg = rowIdx % 2 === 1 ? 'bg-[#FAFAFA]' : 'bg-white';
                return (
                  <tr key={row.staff_id} className={`border-b border-gray-100/80 ${rowBg} hover:bg-gray-50/60 transition-colors duration-200 ease-out`}>
                    <td
                      className={`p-3 text-sm font-medium text-[#1a1a2e] sticky left-0 z-10 min-w-[120px] max-w-[120px] truncate shadow-[2px_0_0_0_rgba(0,0,0,0.03)] ${rowBg}`}
                      title={row.staff_name}
                    >
                      {row.staff_name}
                    </td>
                    {row.days?.map((day) => (
                      <DayCell
                        key={day.date}
                        day={day}
                        todayStr={todayStr}
                        rowStaffId={row.staff_id}
                        rowStaffName={row.staff_name}
                        popoverDay={popoverDay}
                        setPopoverDay={setPopoverDay}
                        onOverride={handleOverride}
                        t={t}
                        formatTime={formatTime}
                        formatDurationHours={formatDurationHours}
                        formatDateShort={formatDateShort}
                      />
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 rounded-[12px] border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-gray-700">
            <span className="inline-flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" aria-hidden />
              {t('full_day')}
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400 shrink-0" aria-hidden />
              {t('half_day')}
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-300 shrink-0" aria-hidden />
              {t('in_progress')}
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400 shrink-0" aria-hidden />
              {t('short_day')}
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-pink-200 shrink-0 ring-1 ring-pink-300/50" aria-hidden />
              {t('absent')}
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0" aria-hidden />
              {t('holiday')}
            </span>
          </div>
        </div>
      </section>

      {attendanceSummary.length > 0 && (
        <section className="mt-6 space-y-4">
          <h2 className="text-[20px] font-bold text-[#1a1a2e] font-poppins tracking-tight">{t('monthly_hours_summary')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {attendanceSummary.map((row) => {
              const todayStat = todayStats[row.staff_id];
              const staffRow = staff.find((s) => s.id === row.staff_id);
              const isOnDuty = todayStat?.is_clocked_in ?? staffRow?.is_clocked_in;
              const thisWeekHours = row.days?.filter((d) => isDateInCurrentWeek(d.date)).reduce((s, d) => s + (d.total_hours || 0), 0) ?? 0;
              const sum = row.summary || {};
              return (
                <Card
                  key={row.staff_id}
                  className={`rounded-[12px] border border-gray-100 shadow-sm transition-shadow duration-200 ease-out hover:shadow-md ${isOnDuty ? 'border-l-[4px] border-l-[#F5A623]' : ''}`}
                >
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-2 pb-3 border-b border-gray-100">
                      <div>
                        <p className="text-lg font-semibold text-[#1a1a2e]">{row.staff_name}</p>
                        {staffRow?.role && (
                          <Badge className={`mt-1 ${roleColors[staffRow.role] || 'bg-gray-100 text-gray-700'}`}>{staffRow.role}</Badge>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 pt-4 text-center sm:text-left">
                      <div>
                        <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-gray-500">{t('hours_today')}</p>
                        <p className="text-[22px] font-bold text-[#1a1a2e] leading-tight mt-1 tabular-nums">{formatDurationHours(todayStat?.hours_today ?? 0)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-gray-500">{t('hours_this_week')}</p>
                        <p className="text-[22px] font-bold text-[#1a1a2e] leading-tight mt-1 tabular-nums">{formatDurationHours(thisWeekHours)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-gray-500">{t('hours_this_month')}</p>
                        <p className="text-[22px] font-bold text-[#1a1a2e] leading-tight mt-1 tabular-nums">{formatDurationHours(sum.total_hours)}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap gap-x-4 gap-y-1 text-[14px] text-gray-500">
                      <span>
                        <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-gray-400">{t('days_worked')}</span>{' '}
                        <span className="font-semibold text-[#1a1a2e] tabular-nums">{sum.days_present ?? 0}/{sum.total_days_in_month ?? 0}</span>
                      </span>
                      <span>
                        <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-gray-400">{t('full_days')}</span>{' '}
                        <span className="font-semibold text-[#1a1a2e] tabular-nums">{sum.full_days ?? 0}</span>
                      </span>
                      <span>
                        <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-gray-400">{t('avg_per_day')}</span>{' '}
                        <span className="font-semibold text-[#1a1a2e] tabular-nums">{sum.avg_hours_per_day != null ? `${Number(sum.avg_hours_per_day).toFixed(2)}h` : '0h'}</span>
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {detailStaff && (
        <StaffDetailPanel
          staff={detailStaff}
          onClose={() => setDetailStaff(null)}
          t={t}
          formatCurrency={formatCurrency}
          formatDurationHours={formatDurationHours}
          formatTime={formatTime}
          formatDateShort={formatDateShort}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#faf8f5] rounded-2xl sm:rounded-3xl max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-poppins text-lg sm:text-xl">{editingId ? t('staff_edit') : t('staff_add')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 sm:mt-4">
            <div>
              <Label className="text-base mb-1.5 block">{t('name')} *</Label>
              <Input data-testid="staff-form-name" className="h-11 sm:h-12 rounded-xl min-h-[48px]" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label className="text-base mb-1.5 block">{t('phone')} *</Label>
              <Input data-testid="staff-form-phone" className="h-11 sm:h-12 rounded-xl min-h-[48px]" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base mb-1.5 block">{t('staff_role')}</Label>
                <select
                  data-testid="staff-form-role"
                  className="w-full h-11 sm:h-12 rounded-xl border border-gray-200 px-3 text-base bg-white min-h-[48px]"
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                >
                  {['Owner', 'Manager', 'Stylist', 'Receptionist', 'Assistant', 'Barber'].map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-base mb-1.5 block">{t('staff_comm')}</Label>
                <Input data-testid="staff-form-commission" type="number" className="h-11 sm:h-12 rounded-xl min-h-[48px]" value={form.commission_rate} onChange={(e) => setForm((f) => ({ ...f, commission_rate: e.target.value }))} />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4 mt-2 space-y-4">
              <p className="text-sm font-semibold text-[#1a1a2e] flex items-center gap-2">
                <Lock className="w-4 h-4" /> {t('app_access_optional_section')}
              </p>

              {editingId && hadLoginWhenOpened && form.has_login ? (
                <div className="space-y-3">
                  <p className="text-sm text-green-800 bg-green-50 border border-green-100 rounded-xl px-3 py-2">
                    {t('staff_has_app_access_email', { email: form.login_email || '—' })}
                  </p>
                  <div>
                    <Label className="text-base mb-1.5 block">{t('login_email')}</Label>
                    <Input
                      type="email"
                      className="h-11 sm:h-12 rounded-xl min-h-[48px]"
                      placeholder="staff@email.com"
                      value={form.login_email}
                      onChange={(e) => setForm((f) => ({ ...f, login_email: e.target.value }))}
                    />
                  </div>
                  {!showAppAccessPasswordField ? (
                    <button
                      type="button"
                      className="text-sm text-[#f0a500] font-medium hover:underline"
                      onClick={() => setShowAppAccessPasswordField(true)}
                    >
                      {t('change_password')}
                    </button>
                  ) : (
                    <div>
                      <Label className="text-base mb-1.5 block">{t('login_password')}</Label>
                      <div className="relative">
                        <Input
                          type={showLoginPassword ? 'text' : 'password'}
                          className="h-11 sm:h-12 rounded-xl min-h-[48px] pr-12"
                          placeholder={t('login_password_placeholder')}
                          value={form.login_password}
                          onChange={(e) => setForm((f) => ({ ...f, login_password: e.target.value }))}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                        >
                          {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => setRemoveLoginConfirmOpen(true)}
                  >
                    {t('disable_login')}
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-gray-200 bg-white/80 px-3 py-3">
                    <div>
                      <Label htmlFor="staff-give-app-access" className="text-base font-medium text-[#1a1a2e] cursor-pointer">
                        {t('give_app_access')}
                      </Label>
                      {!form.has_login && (
                        <p className="text-sm text-gray-500 mt-1">{t('app_access_cannot_login')}</p>
                      )}
                    </div>
                    <Switch
                      id="staff-give-app-access"
                      checked={form.has_login}
                      onCheckedChange={(v) => {
                        setForm((f) => ({
                          ...f,
                          has_login: v,
                          login_password: v ? f.login_password : '',
                        }));
                        if (!v) setShowLoginPassword(false);
                      }}
                    />
                  </div>
                  {form.has_login && (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-base mb-1.5 block">{t('login_email')} *</Label>
                        <Input
                          type="email"
                          data-testid="staff-login-email"
                          className="h-11 sm:h-12 rounded-xl min-h-[48px]"
                          placeholder="staff@email.com"
                          value={form.login_email}
                          onChange={(e) => setForm((f) => ({ ...f, login_email: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label className="text-base mb-1.5 block">{t('login_password')} *</Label>
                        <div className="relative">
                          <Input
                            type={showLoginPassword ? 'text' : 'password'}
                            data-testid="staff-login-password"
                            className="h-11 sm:h-12 rounded-xl min-h-[48px] pr-12"
                            placeholder={t('login_password_placeholder')}
                            value={form.login_password}
                            onChange={(e) => setForm((f) => ({ ...f, login_password: e.target.value }))}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => setShowLoginPassword(!showLoginPassword)}
                          >
                            {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        {t('app_access_login_blurb', { role: form.role, access: roleAccessDescription(form.role, t) })}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-11 sm:h-12 px-6 rounded-xl text-base w-full sm:w-auto min-h-[48px]">{t('cancel')}</Button>
            <Button data-testid="staff-form-save-btn" onClick={handleSave} disabled={saving} className="h-11 sm:h-12 px-8 rounded-xl bg-[#1a1a2e] hover:bg-[#2d2d44] text-white text-base w-full sm:w-auto min-h-[48px]">
              {saving ? t('saving') : t('save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins">{t('staff_del_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('staff_del_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl min-h-[48px]">{t('del_cancel')}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-staff-btn" onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-xl min-h-[48px]">{t('del_confirm_btn')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={removeLoginConfirmOpen} onOpenChange={setRemoveLoginConfirmOpen}>
        <AlertDialogContent className="rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins">{t('disable_login')}</AlertDialogTitle>
            <AlertDialogDescription>{t('remove_app_access_confirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl min-h-[48px]">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveStaffLogin} className="bg-red-500 hover:bg-red-600 rounded-xl min-h-[48px]">
              {t('disable_login')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
